/**
 * Daily hours of a multi-day event (migration 2026-10-27-1120, table `event_day_hours`).
 *
 * An event is stored as ONE start instant and ONE end instant. A weekend show that opens Saturday 9 AM and closes
 * Sunday 3 PM therefore reads "9 AM - 3 PM", and staff assumed Saturday also closed at 3 PM when it ran to 5 PM.
 * `event_day_hours` stores, per event-local date, the hours the event is actually open that day (wall clock in the
 * event's time zone). Rows are optional: with none, every screen shows what it showed before.
 *
 * Rules shared with the database guard (`_event_day_hours_guard`):
 *   - only timed events (not all-day, not date TBD) with an end later than the start;
 *   - spanning 2..14 event-local dates (14 = the MSR date model's service-day cap);
 *   - closing after opening on the same day (no overnight hours);
 *   - the time zone is the event's, blank/unknown = America/Chicago (`eventZone`, same as the server).
 * Extra rule kept by the client only: an event that ends exactly at midnight cannot use daily hours (its last date
 * would have no hours); the form explains this.
 *
 * Day 1's opening time is always the event's start time and the last day's closing time is always its end time --
 * the form shows them read-only and the display takes them from the event, so the two can never disagree.
 *
 * Pure (no Date.now): unit-tested in eventDayHours.test.ts.
 */
import { eventZone } from "./eventServiceDays";

export const MAX_DAILY_HOURS_DAYS = 14;

/** One day's hours. Times are "HH:MM" (24 h), dates "YYYY-MM-DD", both event-local. */
export interface DayHours {
  service_date: string;
  opens_at: string;
  closes_at: string;
}

export interface DayHoursEvent {
  start_time?: string | null;
  end_time?: string | null;
  timezone?: string | null;
  all_day?: boolean | null;
  date_tbd?: boolean | null;
}

export type DailyHoursUnavailableReason =
  | "no_start"
  | "date_tbd"
  | "all_day"
  | "no_end"
  | "end_before_start"
  | "single_day"
  | "ends_midnight"
  | "too_long";

export type DailyHoursApplicability =
  | {
      ok: true;
      zone: string;
      dates: string[];
      /** Event start's local clock "HH:MM" = day 1 opening. */
      startClock: string;
      /** Event end's local clock "HH:MM" = last day closing. */
      endClock: string;
    }
  | { ok: false; reason: DailyHoursUnavailableReason; dayCount?: number };

/** Plain-language reason the daily-hours section is not offered (shown in the form). */
export const DAILY_HOURS_REASON_TEXT: Record<
  DailyHoursUnavailableReason,
  string
> = {
  no_start: "Set the event's start date and time first.",
  date_tbd: "Not available while the date is TBD.",
  all_day:
    'Not available for all-day events. Turn off "All day" to set opening hours for each day.',
  no_end: "Set an end date and time first.",
  end_before_start: "The end must be after the start.",
  single_day: "Only needed when the event runs over more than one day.",
  ends_midnight:
    "Not available when the event ends exactly at midnight. End it at 11:59 PM instead to set hours for each day.",
  too_long: `Only available for events of ${MAX_DAILY_HOURS_DAYS} days or fewer.`,
};

const PARTS = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = PARTS.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    PARTS.set(tz, f);
  }
  return f;
}

/** Local date "YYYY-MM-DD" and clock "HH:MM" of an instant in `tz`. null for a missing/invalid instant. */
export function localDateAndClock(
  iso: string | null | undefined,
  tz: string,
): { date: string; clock: string } | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return null;
  }
  const p = partsFormatter(tz).formatToParts(d);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? "00";
  const hour = String(Number(get("hour")) % 24).padStart(2, "0");
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    clock: `${hour}:${get("minute")}`,
  };
}

/** "YYYY-MM-DD" + n days (calendar arithmetic, zone-free). */
export function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round(
    (Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000,
  );
}

/** Whether daily hours apply to this event, and its local dates when they do. */
export function dailyHoursApplicability(
  ev: DayHoursEvent,
): DailyHoursApplicability {
  if (ev.date_tbd) {
    return { ok: false, reason: "date_tbd" };
  }
  if (ev.all_day) {
    return { ok: false, reason: "all_day" };
  }
  const zone = eventZone(ev.timezone);
  const start = localDateAndClock(ev.start_time, zone);
  if (!start) {
    return { ok: false, reason: "no_start" };
  }
  const end = localDateAndClock(ev.end_time, zone);
  if (!end) {
    return { ok: false, reason: "no_end" };
  }
  if (
    new Date(ev.end_time as string).getTime() <=
    new Date(ev.start_time as string).getTime()
  ) {
    return { ok: false, reason: "end_before_start" };
  }
  return fromLocal(zone, start.date, start.clock, end.date, end.clock);
}

/**
 * The same decision from the event FORM's local fields (dates/times already in the event's zone), so the form never
 * converts back and forth. Uses the save path's own end rule: empty end date = the start date.
 */
export function dailyHoursApplicabilityLocal(f: {
  startDate?: string | null;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
  allDay?: boolean | null;
  dateTbd?: boolean | null;
  timezone?: string | null;
}): DailyHoursApplicability {
  if (f.dateTbd) {
    return { ok: false, reason: "date_tbd" };
  }
  if (f.allDay) {
    return { ok: false, reason: "all_day" };
  }
  const startClock = normalizeClock(f.startTime);
  if (!f.startDate || !startClock) {
    return { ok: false, reason: "no_start" };
  }
  const endDate = f.endDate || f.startDate;
  const endClock = normalizeClock(f.endTime);
  if (!endClock) {
    return { ok: false, reason: "no_end" };
  }
  if (`${endDate}T${endClock}` <= `${f.startDate}T${startClock}`) {
    return { ok: false, reason: "end_before_start" };
  }
  return fromLocal(
    eventZone(f.timezone),
    f.startDate,
    startClock,
    endDate,
    endClock,
  );
}

function fromLocal(
  zone: string,
  startDate: string,
  startClock: string,
  endDate: string,
  endClock: string,
): DailyHoursApplicability {
  const span = daysBetween(startDate, endDate);
  if (span < 1) {
    return { ok: false, reason: "single_day", dayCount: 1 };
  }
  if (span + 1 > MAX_DAILY_HOURS_DAYS) {
    return { ok: false, reason: "too_long", dayCount: span + 1 };
  }
  if (endClock === "00:00") {
    return { ok: false, reason: "ends_midnight", dayCount: span + 1 };
  }
  const dates: string[] = [];
  for (let i = 0; i <= span; i++) {
    dates.push(addDays(startDate, i));
  }
  return { ok: true, zone, dates, startClock, endClock };
}

/** "HH:MM" (or "HH:MM:SS" from PostgREST) -> "HH:MM"; "" when not a valid clock. */
export function normalizeClock(t: string | null | undefined): string {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec((t ?? "").trim());
  if (!m) {
    return "";
  }
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) {
    return "";
  }
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

/**
 * Starting hours for each date: day 1 opens at the event start, the last day closes at the event end, and every
 * other boundary uses the same clock (today's implied reading). A boundary that would make a day close before it
 * opens is left blank for the operator to fill.
 */
export function defaultDayHours(
  app: Extract<DailyHoursApplicability, { ok: true }>,
): DayHours[] {
  const uniformOk = app.startClock < app.endClock;
  return app.dates.map((date, i) => {
    const first = i === 0;
    const last = i === app.dates.length - 1;
    const opens = first ? app.startClock : uniformOk ? app.startClock : "";
    const closes = last ? app.endClock : uniformOk ? app.endClock : "";
    return { service_date: date, opens_at: opens, closes_at: closes };
  });
}

/**
 * Fit a saved/edited set to the event's CURRENT dates: keep hours for dates still in the event, add starting hours
 * for new dates, drop dates the event no longer covers, and pin day 1's opening / the last day's closing to the
 * event's start / end. Reports what changed so the form can say so.
 */
export function reconcileDayHours(
  app: Extract<DailyHoursApplicability, { ok: true }>,
  existing: DayHours[],
): { days: DayHours[]; added: string[]; dropped: string[] } {
  const byDate = new Map(existing.map((d) => [d.service_date, d]));
  const defaults = defaultDayHours(app);
  const added: string[] = [];
  const days = defaults.map((def, i) => {
    const have = byDate.get(def.service_date);
    if (!have) {
      added.push(def.service_date);
    }
    const base = have
      ? {
          service_date: def.service_date,
          opens_at: normalizeClock(have.opens_at),
          closes_at: normalizeClock(have.closes_at),
        }
      : def;
    return {
      service_date: def.service_date,
      opens_at: i === 0 ? app.startClock : base.opens_at,
      closes_at: i === defaults.length - 1 ? app.endClock : base.closes_at,
    };
  });
  const inRange = new Set(app.dates);
  const dropped = existing
    .map((d) => d.service_date)
    .filter((d) => !inRange.has(d));
  return { days, added, dropped };
}

/** Per-date problems ({} = valid). */
export function validateDayHours(days: DayHours[]): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const d of days) {
    const o = normalizeClock(d.opens_at);
    const c = normalizeClock(d.closes_at);
    if (!o || !c) {
      errors[d.service_date] = "Enter an opening and a closing time.";
    } else if (c <= o) {
      errors[d.service_date] =
        "Closing time must be after opening time (hours past midnight are not supported).";
    }
  }
  return errors;
}

/**
 * The hours to DISPLAY for an event, or null to keep today's display. Null when daily hours do not apply, when the
 * stored set does not cover every current date (e.g. the event's dates changed elsewhere), or when the event's own
 * start/end make a day invalid. Rows outside the event's current dates are ignored.
 */
export function resolveDailyHours(
  ev: DayHoursEvent,
  rows:
    | ReadonlyArray<{
        service_date: string;
        opens_at: string;
        closes_at: string;
      }>
    | null
    | undefined,
): DayHours[] | null {
  if (!rows || rows.length === 0) {
    return null;
  }
  const app = dailyHoursApplicability(ev);
  if (!app.ok) {
    return null;
  }
  const byDate = new Map(rows.map((r) => [r.service_date, r]));
  if (!app.dates.every((d) => byDate.has(d))) {
    return null;
  }
  const { days } = reconcileDayHours(
    app,
    app.dates.map((d) => byDate.get(d)!),
  );
  return Object.keys(validateDayHours(days)).length === 0 ? days : null;
}

/** "17:00" -> "5 PM", "17:30" -> "5:30 PM", "00:00" -> "12 AM", "12:00" -> "12 PM". */
export function formatClock(t: string): string {
  const c = normalizeClock(t);
  if (!c) {
    return "";
  }
  const [h, m] = c.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0
    ? `${h12} ${suffix}`
    : `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

const DAY_LABEL = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  weekday: "short",
  month: "short",
  day: "numeric",
});

/** "2026-10-24" -> "Sat, Oct 24" (calendar date, no zone shift). */
export function formatDayLabel(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return DAY_LABEL.format(new Date(Date.UTC(y, m - 1, d)));
}

/** "Sat, Oct 24: 9 AM–5 PM" */
export function formatDayHours(d: DayHours): string {
  return `${formatDayLabel(d.service_date)}: ${formatClock(d.opens_at)}–${formatClock(d.closes_at)}`;
}

/** "Sat, Oct 24: 9 AM–5 PM · Sun, Oct 25: 9 AM–3 PM" */
export function formatDailyHours(days: DayHours[]): string {
  return days.map(formatDayHours).join(" · ");
}
